import { match } from '@frontend/duck-tape';
import { useState } from '@frontend/react';
import { usePaymentStepContext, usePaywallContext } from '@frontend/react-domain';
import { Button } from '@frontend/web-react';
import { usePaywallPricingContext } from './PaywallPricingContext';

export type PaywallNextButtonProps = {
  className?: string;
};

export const PaywallNextButton = ({ className }: PaywallNextButtonProps) => {
  const { paywallActions, paywallState, track } = usePaywallContext();
  const { allFieldsComplete, isPaymentProcessing, submitPayment } = usePaymentStepContext();
  const { pricingState, selectedPlanData } = usePaywallPricingContext();
  const [isProcessing, setIsProcessing] = useState(false);

  const {
    navigation: { currentStep, paywallSessionId },
    purchase: { selectedCreditsPackage, selectedPlanType, selectedPurchaseType },
  } = paywallState;

  const {
    navigation: { incrementStep },
    purchase: { onConfirmSubscriptionChoice, onFinish },
  } = paywallActions;

  const handleClick = () => {
    if (isProcessing) {
      return;
    }

    match(currentStep)
      .with('selectPurchaseType', () => {
        incrementStep();
      })
      .with('selectSubscriptionPlan', () => {
        if (selectedPlanType) {
          track({
            data: {
              paywallSessionId,
              planCode: selectedPlanData?.planCode,
              platform: 'web',
              purchaseType: selectedPurchaseType,
              step: currentStep,
            },
            event: 'continueToPaymentSelected',
            topic: 'paywall',
          });
          onConfirmSubscriptionChoice(selectedPlanType);
          incrementStep();
        }
      })
      .with('checkout', async () => {
        setIsProcessing(true);
        if (selectedPurchaseType) {
          track({
            data: {
              paywallSessionId,
              planCode: selectedPlanData?.planCode,
              platform: 'web',
              purchaseType: selectedPurchaseType,
              step: currentStep,
            },
            event: 'paymentSubmitAttempted',
            topic: 'paywall',
          });
        }
        await submitPayment();
        setIsProcessing(false);
      })
      .with('success', async () => {
        track({
          data: {
            paywallSessionId,
            planCode: selectedPlanData?.planCode,
            platform: 'web',
            purchaseType: selectedPurchaseType,
          },
          event: 'onboardingCompleteCTA',
          topic: 'paywall',
        });
        await onFinish();
      })
      .otherwise(() => {});
  };

  const buttonText = match(currentStep)
    .with('selectPurchaseType', () => 'Continue')
    .with('selectSubscriptionPlan', () => 'Continue')
    .with('checkout', () => (isPaymentProcessing || isProcessing ? 'Processing...' : 'Get Started'))
    .with('success', () => 'Welcome to Duckbill')
    .otherwise(() => 'Continue');

  const isDisabled = match(currentStep)
    .with(
      'selectPurchaseType',
      () => !selectedPurchaseType || (selectedPurchaseType === 'ALaCarteCredits' && !selectedCreditsPackage),
    )
    .with('selectSubscriptionPlan', () => !selectedPlanType || !selectedPlanData || pricingState === 'transitioning')
    .with('checkout', () => !allFieldsComplete || isPaymentProcessing || isProcessing)
    .with('success', () => false)
    .otherwise(() => false);

  return (
    <Button className={className} disabled={isDisabled} onClick={handleClick} size="lg" variant="primary">
      {buttonText}
    </Button>
  );
};
